import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Check, FileDown, ShieldCheck, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useI18n } from "@/lib/i18n";
import { CONVERSION_LOCALES } from "@/locales/conversion";

const PAGES = {
  "letter-of-intent": {
    code: "LOI", name: "Letter of Intent (LOI)",
    headline: "Create a professional Letter of Intent from your transaction terms.",
    description: "Structure buyer and seller details, commodity, quantity, Incoterms, payment terms, validity and the non-binding framework in one editable LOI draft.",
    fields: ["Buyer and seller", "Commodity and specification", "Quantity and tolerance", "Incoterms and destination", "Price and payment terms", "Validity and signature block"],
    outcomes: ["Business-grade LOI wording", "Missing information clearly marked", "Editable Word and PDF output"],
  },
  icpo: {
    code: "ICPO", name: "Irrevocable Corporate Purchase Order",
    headline: "Prepare an ICPO using the commercial terms of your deal.",
    description: "Turn structured buyer, commodity, delivery and banking parameters into a reviewable ICPO draft without rebuilding the document in Word each time.",
    fields: ["Buyer corporate details", "Product and specification", "Quantity and schedule", "Price or pricing formula", "Incoterms", "Payment instrument and bank placeholders"],
    outcomes: ["Consistent purchase-order structure", "Transaction-specific clauses", "Editable Word and PDF output"],
  },
  fco: {
    code: "FCO", name: "Full Corporate Offer",
    headline: "Build a structured Full Corporate Offer in one workspace.",
    description: "Prepare an FCO with the seller, commodity, price, payment, delivery, validity and transaction procedure organised for professional review.",
    fields: ["Seller and buyer", "Commodity specification", "Quantity and price", "Delivery schedule", "Payment and bank terms", "Procedure, validity and signatures"],
    outcomes: ["Complete commercial offer structure", "Reusable company and deal data", "Editable Word and PDF output"],
  },
  "letter-of-credit": {
    code: "LC", name: "Letter of Credit Draft",
    headline: "Prepare and review documentary credit terms before bank submission.",
    description: "Organise applicant, beneficiary, amount, shipment dates, ports, goods, required documents and UCP-related terms in a controlled LC drafting workflow.",
    fields: ["Applicant and beneficiary", "Amount, currency and tolerance", "Expiry and shipment dates", "Ports and goods description", "Required document set", "Availability, confirmation and charges"],
    outcomes: ["Structured LC clause draft", "Document checklist and red-flag review", "Editable Word and PDF output"],
  },
  "shipping-correspondence": {
    code: "SHIPPING", name: "Shipping & Chartering Correspondence",
    headline: "Draft recaps, laytime positions and formal shipping correspondence.",
    description: "Keep vessel, cargo, ports, laycan, freight, demurrage and claim facts connected to the transaction while preparing professional shipping documents.",
    fields: ["Vessel and parties", "Cargo and quantity", "Load and discharge ports", "Laycan and laytime", "Freight and demurrage", "Claim facts and requested action"],
    outcomes: ["Fixture and correspondence structure", "Deal-based drafting and version history", "Editable Word and PDF output"],
  },
};

const UI = {
  en: { label: "PROFESSIONAL TRADE DOCUMENT", included: "What the workflow covers", result: "What you receive", how: "From deal terms to an editable document", steps: ["Choose the document", "Add transaction data", "Generate and review", "Export Word or PDF"], start: "Create 3 Documents Free", pricing: "View Pricing", noCard: "No card required", languages: "EN / IT / DE / ES / ZH / RU", finalTitle: "Create the document inside your transaction workspace.", note: "TradeDocs provides professional drafting assistance, not legal or banking advice. Users remain responsible for reviewing documents before use." },
  it: { label: "DOCUMENTO COMMERCIALE PROFESSIONALE", included: "Cosa copre il flusso", result: "Cosa ricevi", how: "Dai termini dell'operazione al documento modificabile", steps: ["Scegli il documento", "Aggiungi i dati", "Genera e revisiona", "Esporta Word o PDF"], start: "Crea 3 documenti gratis", pricing: "Vedi prezzi", note: "TradeDocs offre assistenza professionale alla redazione, non consulenza legale o bancaria. L'utente deve revisionare i documenti prima dell'uso." },
  de: { label: "PROFESSIONELLES HANDELSDOKUMENT", included: "Inhalt des Workflows", result: "Ihr Ergebnis", how: "Von Geschäftsdaten zum bearbeitbaren Dokument", steps: ["Dokument wählen", "Geschäftsdaten eingeben", "Erstellen und prüfen", "Word oder PDF exportieren"], start: "3 Dokumente kostenlos", pricing: "Preise ansehen", note: "TradeDocs unterstützt die professionelle Dokumenterstellung, ersetzt jedoch keine Rechts- oder Bankberatung. Dokumente sind vor Verwendung zu prüfen." },
};

export default function TemplateMarketingPage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const page = (CONVERSION_LOCALES[lang]?.templatePages || PAGES)[slug];
  const c = CONVERSION_LOCALES[lang]?.templateUi || UI[lang] || UI.en;

  useEffect(() => {
    if (!page) return undefined;
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content");
    const canonical = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute("href");
    document.title = `${page.name} Template & AI Drafting | TradeDocs AI Desk`;
    description?.setAttribute("content", page.description);
    canonical?.setAttribute("href", `https://tradedocs.online/templates/${slug}`);
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.dataset.tradedocsTemplate = slug;
    schema.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "SoftwareApplication",
      name: `TradeDocs ${page.name}`, applicationCategory: "BusinessApplication",
      operatingSystem: "Web", url: `https://tradedocs.online/templates/${slug}`,
      description: page.description,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: "Three documents free; paid plans available" },
      provider: { "@type": "Organization", name: "AIT Technologies GmbH" },
    });
    document.head.appendChild(schema);
    return () => {
      document.title = previousTitle;
      if (previousDescription) description?.setAttribute("content", previousDescription);
      if (previousCanonical) canonical?.setAttribute("href", previousCanonical);
      schema.remove();
    };
  }, [page, slug]);

  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="landing-page min-h-[100dvh] overflow-x-clip">
      <Navbar />
      <main>
        <section className="pt-28 sm:pt-36 md:pt-44 pb-16 sm:pb-24 hairline-bottom">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10">
            <div className="kicker mb-5">— {c.label} · {page.code}</div>
            <div className="grid lg:grid-cols-[1.3fr_.7fr] gap-10 items-start">
              <div>
                <h1 className="font-serif text-[2.4rem] sm:text-5xl md:text-6xl leading-[1.03] tracking-tight mb-6">{page.headline}</h1>
                <p className="text-lg text-[var(--td-text-2)] leading-relaxed max-w-3xl mb-8">{page.description}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/signup" className="btn-gold inline-flex items-center justify-center gap-2">{c.start}<ArrowRight size={15} /></Link>
                  <Link to="/pricing" className="btn-ghost inline-flex items-center justify-center">{c.pricing}</Link>
                </div>
                <div className="mt-5 text-[10px] font-mono uppercase tracking-widest text-[var(--td-text-3)]">{c.noCard || UI.en.noCard} · {c.languages || UI.en.languages} · Word / PDF</div>
              </div>
              <aside className="card-surface p-6 sm:p-8 border-t-2 border-t-[var(--td-gold)]">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-5">{c.result}</div>
                <div className="space-y-4">{page.outcomes.map((item) => <div key={item} className="flex gap-3 text-sm text-[var(--td-text-2)]"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" />{item}</div>)}</div>
              </aside>
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-2 gap-12">
            <div><div className="kicker mb-4">— {c.included}</div><h2 className="font-serif text-3xl sm:text-5xl mb-8">{page.name}</h2><div className="grid sm:grid-cols-2 gap-3">{page.fields.map((field) => <div key={field} className="card-surface p-4 flex gap-3 text-sm"><Check size={14} className="text-[var(--td-gold)] shrink-0 mt-0.5" />{field}</div>)}</div></div>
            <div><div className="kicker mb-4">— {c.how}</div><div className="space-y-3">{c.steps.map((step, index) => <div key={step} className="card-surface p-5 flex items-center gap-4"><span className="font-mono text-xs text-[var(--td-gold)]">0{index + 1}</span><span className="font-serif text-xl">{step}</span>{index === 3 ? <FileDown size={17} className="text-[var(--td-gold)] ml-auto" /> : <Sparkles size={17} className="text-[var(--td-text-3)] ml-auto" />}</div>)}</div></div>
          </div>
        </section>

        <section className="py-16 sm:py-20 hairline-top">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 text-center">
            <ShieldCheck size={24} className="text-[var(--td-gold)] mx-auto mb-5" />
            <h2 className="font-serif text-3xl sm:text-5xl mb-6">{c.finalTitle || UI.en.finalTitle}</h2>
            <Link to="/signup" className="btn-gold inline-flex items-center justify-center gap-2">{c.start}<ArrowRight size={15} /></Link>
            <p className="mt-8 text-xs text-[var(--td-text-3)] leading-relaxed">{c.note}</p>
          </div>
        </section>
      </main>
    </div>
  );
}
